// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import {
  AssetBrand,
  Heading,
  Table,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { Switch, Tooltip } from '@mui/material';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';

export type RebalancePortfolioPosition = {
  ticker: string;
  qty: number;
  weight: number;
  details?: AssetsInfoResp;
};

export interface RebalancePortfolioTable {
  readonly data: RebalancePortfolioPosition[];
  readonly currency: AccountCurrency;
  readonly enableTransfers?: boolean;
  readonly onEnableTransfersChange?: (checked: boolean) => void;
}

export const RebalancePortfolioTable: FC<RebalancePortfolioTable> = ({
  data,
  currency,
  enableTransfers = false,
  onEnableTransfersChange,
}) => {
  const { t } = useTranslation();
  const { openInvestmentPopup } = useInvestmentPopup();

  const totalWeight = useMemo(
    () =>
      (Math.round(data.map(asset => asset.weight).reduce((a, b) => a + b, 0)) *
        100) /
      100,
    [data],
  );

  return (
    <div className="w-full">
      <div className="mb-9 flex flex-col">
        <div className="mb-7 flex justify-between">
          <Heading
            title={t('rebalance.current-portfolio', { ns: 'rebalance' })}
            variant="h3"
          />
        </div>

        {onEnableTransfersChange && (
          <div className="mb-6 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Tooltip
                title={t('rebalance.enable-transfers-description', {
                  ns: 'rebalance',
                })}
                placement="top"
                arrow
              >
                <span className="text-sm text-primary-medium">
                  {t('rebalance.enable-transfers', { ns: 'rebalance' })}
                </span>
              </Tooltip>
            </div>

            <Switch
              checked={enableTransfers}
              onChange={event => onEnableTransfersChange(event.target.checked)}
              data-testid="enable-transfers-switch"
              color="primary"
              disableRipple
              inputProps={{
                'aria-label': 'controlled',
                role: 'switch',
                'aria-checked': enableTransfers,
              }}
              className="!h-[19px] !w-[38px] !p-0"
              classes={{
                colorPrimary: '!text-control-thumb',
                track: '!bg-control-track !rounded-full ',
                thumb: '!h-[18px] !w-[18px] box-border ',
                switchBase: '!p-0 !rounded-full !m-0',
              }}
            />
          </div>
        )}
      </div>

      <table className="w-full text-base uppercase leading-6 text-border-dark">
        <thead>
          <tr className="[&>th>div]:mb-4 [&>th]:font-normal">
            <th>
              <div>
                <Translation i18nKey="common.name" ns="common" />
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.ticker" ns="common" />
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.isin" ns="common" />
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.investment" ns="common" />
              </div>
            </th>
            <th>
              <div>
                <Translation i18nKey="common.weight" ns="common" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map(({ ticker, qty, weight, details = {} }) => (
            <tr
              key={ticker}
              className="text-primary-medium [&>td>div]:mb-4 [&>td>div]:h-[24px]"
            >
              <td>
                <div className="max-w-[200px]">
                  <AssetBrand
                    ticker={ticker}
                    label={details?.name}
                    logoUrl={(details as Stock)?.url_logo}
                    url={(details as Stock)?.url_morningstar}
                    assetType={details?.asset_type ?? 'stock'}
                    analyzePopup={openInvestmentPopup}
                    watchlistButton={
                      <WatchlistToggleButton
                        ticker={ticker}
                        assetType={details?.asset_type ?? 'stock'}
                      />
                    }
                  />
                </div>
              </td>
              <td>
                <div>
                  <span data-testid={`ticker-${ticker}`}>
                    {details?.ticker ?? ticker}
                  </span>
                </div>
              </td>
              <td>
                <div>
                  <span>{details?.isin ?? '-'}</span>
                </div>
              </td>
              <td>
                <div>
                  <Table.Data
                    value={qty}
                    format="currency"
                    formatOptions={{ currency: currency }}
                  />
                </div>
              </td>
              <td>
                <div>
                  <Table.Data
                    value={weight / 100}
                    format="percentage"
                    formatOptions={{
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 1,
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}>
              <hr className="mb-4 border-sidebar-text" />
            </td>
          </tr>
          <tr>
            <td>Total</td>
            <td colSpan={3}></td>
            <td className="font-semibold text-primary">
              <Table.Data
                value={totalWeight / 100}
                format="percentage"
                formatOptions={{
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 1,
                }}
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
